import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEventCallback } from "usehooks-ts";

import { IconMegaphone } from "@btnet/blueprint-icons";
import { BTText } from "@btnet/core-components/BTText";
import type { WhatsNewItemResponse } from "@btnet/services-btnet/handlers/models/whatsNewItemResponse";
import {
    getWhatsNewGetV2QueryKey,
    getWhatsNewLatestGetV2QueryKey,
    whatsNewGetV2,
} from "@btnet/services-btnet/handlers/whats-new/whats-new";
import { cn } from "@btnet/utilities-core";

import { queryClient } from "helpers/queryClient";

import { useTracking } from "utilities/analytics/analytics";

import { withErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";

import { WhatsNewCardList, WhatsNewCardListSkeleton } from "entity/whatsNew/panel/WhatsNewCardList";
import { getWhatsNewLastSeen, setWhatsNewLastSeen } from "entity/whatsNew/whatsNew.storage";

/** Sent as Limit. Must not exceed the server's WhatsNewValidationConstants.MaximumPageSize (20), or every load 400s. */
const pageSize = 20;

interface IWhatsNewPanelProps {
    builderId: number;
    /** Scopes the device-local watermark, so shared computers don't share the dot. */
    userId: number | null;
    onClose?: () => void;
}

export function WhatsNewPanelInternal({
    builderId,
    userId,
    onClose,
}: Readonly<IWhatsNewPanelProps>) {
    const panelRef = useRef<HTMLElement>(null);
    const { trackEvent } = useTracking();

    const request = useMemo(() => ({ Limit: pageSize }), []);

    const whatsNewQuery = useInfiniteQuery({
        queryKey: getWhatsNewGetV2QueryKey(request),
        queryFn: ({ pageParam = undefined }) =>
            whatsNewGetV2({ ...request, NextCursor: pageParam as string | undefined }),
        refetchOnWindowFocus: false,
        // Typed as the cursor model, but the server sends it as an opaque string to pass back as-is.
        getNextPageParam: (lastPage) => lastPage.data?.nextCursor ?? null,
    });

    if (whatsNewQuery.isError && !whatsNewQuery.data) {
        // Hand it to the error boundary, which needs a real Error.
        throw whatsNewQuery.error instanceof Error
            ? whatsNewQuery.error
            : new Error("Could not load what's new");
    }

    const items: WhatsNewItemResponse[] = useMemo(
        () => whatsNewQuery.data?.pages.flatMap((page) => page.data.data) ?? [],
        [whatsNewQuery.data],
    );

    // Read once: opening the panel moves the watermark, and the dots must stay until it closes.
    const [seenBefore] = useState(() => getWhatsNewLastSeen(builderId, userId));
    const isUnread = useCallback(
        (item: WhatsNewItemResponse) =>
            userId != null && (seenBefore === null || item.publishedDateUtc.getTime() > seenBefore),
        [userId, seenBefore],
    );
    const newCount = items.filter(isUnread).length;

    const newestPublishedDate = items[0]?.publishedDateUtc ?? null;

    // The newest date shown, not "now", so a post that turns new while it's open stays unseen.
    useEffect(() => {
        if (newestPublishedDate == null) {
            return;
        }

        setWhatsNewLastSeen(builderId, userId, newestPublishedDate);
        // So the nav dot re-checks.
        void queryClient.invalidateQueries({ queryKey: getWhatsNewLatestGetV2QueryKey() });
    }, [builderId, userId, newestPublishedDate]);

    // On mount/unmount, so every way of closing is counted.
    const trackOpen = useEventCallback(() => {
        trackEvent({ event: "PopoverOpen", uniqueId: "whatsNewPanelOpen" });
    });
    const trackClose = useEventCallback(() => {
        trackEvent({
            // PopoverClose is missing from the analytics event union - same cast as notifications.
            event: "PopoverClose" as "PopoverOpen",
            uniqueId: "whatsNewPanelClose",
        });
    });
    useEffect(() => {
        trackOpen();
        return trackClose;
    }, [trackOpen, trackClose]);

    // antd gives the dropdown no focus; take it so Escape works. The 200ms delay is copied from
    // NotificationPanel. Keyed on isDropdown because onClose is a new arrow each render.
    const isDropdown = onClose !== undefined;
    useEffect(() => {
        if (!isDropdown) {
            return undefined;
        }

        const focusTimeout = setTimeout(() => {
            if (!panelRef.current?.contains(document.activeElement)) {
                panelRef.current?.focus({ preventScroll: true });
            }
        }, 200);

        return () => {
            clearTimeout(focusTimeout);
        };
    }, [isDropdown]);

    // Stable, so the row memo holds.
    const handleCtaClicked = useEventCallback((item: WhatsNewItemResponse) => {
        // Keyed on the id: authors' link text varies.
        trackEvent({
            event: "LinkClick",
            uniqueId: "whatsNewCtaClick",
            extraInfo: { whatsNewItemId: item.id },
        });
    });

    // Its own event, so GTM can see how often the video is opened.
    const handleVideoClicked = useEventCallback((item: WhatsNewItemResponse) => {
        trackEvent({
            event: "LinkClick",
            uniqueId: "whatsNewVideoClick",
            extraInfo: { whatsNewItemId: item.id },
        });
    });

    const handleKeyDown = onClose
        ? (e: React.KeyboardEvent) => {
              e.stopPropagation();
              if (e.key === "Escape") {
                  onClose();
              }
          }
        : undefined;

    return (
        <section
            // The antd dropdown overlay has no surface of its own, so the panel carries one or the
            // page shows through. Focused by script on open, so no outline ring around the panel.
            className={cn(
                "flex cursor-default flex-col bg-surface-default text-fg-default focus:outline-none",
                // Matches the notification panel so the two nav popovers read as a set.
                { "w-110 rounded-sm shadow-floating": !!onClose },
            )}
            data-testid="whatsNewPanel"
            aria-label="What’s New"
            // Capture, not onKeyDown: an a11y rule bans key handlers on a section. Nothing inside
            // the panel has its own key handler, so stopping the event here hides nothing.
            onKeyDownCapture={handleKeyDown}
            ref={panelRef}
            // Focusable only by script, so Escape reaches the key handler.
            tabIndex={-1}
        >
            <div className="flex items-center justify-between border-b border-default py-3.5 pr-3 pl-5">
                <BTText text="What’s New" size="distinct-lg" />
                {newCount > 0 && (
                    <BTText
                        testid="whatsNewNewCount"
                        text={`${newCount} new`}
                        size="normal-md"
                        className="text-fg-secondary"
                    />
                )}
            </div>

            {whatsNewQuery.isLoading && <WhatsNewCardListSkeleton />}

            {whatsNewQuery.data && (
                <WhatsNewCardList
                    items={items}
                    hasMore={whatsNewQuery.hasNextPage ?? false}
                    isLoadingMore={whatsNewQuery.isFetchingNextPage}
                    isUnread={isUnread}
                    onLoadMore={whatsNewQuery.fetchNextPage}
                    onCtaClicked={handleCtaClicked}
                    onVideoClicked={handleVideoClicked}
                    // Min height is the list's height in the design, so an empty panel doesn't
                    // shrink to a strip. The extra bottom padding lifts the message above center.
                    emptyStateComponent={
                        <div className="flex min-h-112.5 flex-col items-center justify-center gap-6 px-3 pb-12 text-center">
                            <IconMegaphone size="3xl" />
                            <div className="flex flex-col gap-1">
                                <BTText text="You’re all caught up" size="distinct-lg" />
                                <BTText
                                    text="We’ll let you know here when there’s a new feature or update."
                                    size="normal-lg"
                                    className="text-fg-secondary"
                                />
                            </div>
                        </div>
                    }
                />
            )}
        </section>
    );
}

export const WhatsNewPanel = withErrorBoundary(WhatsNewPanelInternal)("Could not load what's new");
